import { useMemo, useState } from 'react';

import { Refresh } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Paper, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useIsMobile } from '@@common';
import { GalleryHeader, GalleryStatistics } from '@@components';

type AuditRecord = {
	id: string;
	timestamp: string;
	type: 'User' | 'Permission' | 'Agent' | 'System';
	actor: string;
	action: string;
	target: string;
	details: string;
};

const auditRecords: AuditRecord[] = [
	{ id: 'audit-001', timestamp: '2026-09-05T10:24:00Z', type: 'User', actor: 'admin@randol.io', action: 'Updated user', target: 'alex@contoso.com', details: 'Updated display name' },
	{ id: 'audit-002', timestamp: '2026-09-05T09:52:00Z', type: 'Permission', actor: 'admin@randol.io', action: 'Assigned role', target: 'alex@contoso.com', details: 'Assigned Administrator role' },
	{ id: 'audit-003', timestamp: '2026-09-04T16:18:00Z', type: 'Agent', actor: 'admin@randol.io', action: 'Assigned role', target: 'Planning agent', details: 'Assigned Editor role' },
	{ id: 'audit-004', timestamp: '2026-09-04T14:05:00Z', type: 'System', actor: 'system', action: 'Created invitation', target: 'new.user@contoso.com', details: 'Invitation expires in 72 hours' },
	{ id: 'audit-005', timestamp: '2026-09-03T11:36:00Z', type: 'Permission', actor: 'admin@randol.io', action: 'Created role', target: 'Reviewer', details: 'Created role with 3 permissions' },
];

export default function AuditView() {
	const isMobile = useIsMobile();
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [type, setType] = useState('');
	const [search, setSearch] = useState('');

	const rows = useMemo(() => {
		const searchValue = search.trim().toLowerCase();
		return auditRecords.filter((record) => {
			const date = record.timestamp.slice(0, 10);
			if (from && date < from) return false;
			if (to && date > to) return false;
			if (type && record.type !== type) return false;
			if (!searchValue) return true;
			return [record.actor, record.action, record.target, record.details, record.type].some((value) => value.toLowerCase().includes(searchValue));
		});
	}, [from, to, type, search]);

	const columns = useMemo<GridColDef<AuditRecord>[]>(
		() => [
			{ field: 'timestamp', headerName: 'Timestamp', minWidth: 180, valueFormatter: (value) => new Date(value).toLocaleString() },
			{ field: 'type', headerName: 'Type', minWidth: 120 },
			{ field: 'actor', headerName: 'Actor', flex: 0.9, minWidth: 160 },
			{ field: 'action', headerName: 'Action', flex: 1, minWidth: 160 },
			{ field: 'target', headerName: 'Target', flex: 1, minWidth: 160 },
			{ field: 'details', headerName: 'Details', flex: 1.4, minWidth: 220 },
		],
		[],
	);

	const resetFilters = () => {
		setFrom('');
		setTo('');
		setType('');
		setSearch('');
	};

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Audit"
				subtitle="Placeholder audit records for the selected tenant."
				utilityActions={<Tooltip title="Clear filters"><IconButton onClick={resetFilters}><Refresh /></IconButton></Tooltip>}
				statistics={<GalleryStatistics items={[{ label: 'Records', value: rows.length }]} />}
			/>

			<Paper sx={{ p: 2, minWidth: 0 }}>
				<Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
					<TextField label="From" type="date" value={from} onChange={(event) => setFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
					<TextField label="To" type="date" value={to} onChange={(event) => setTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
					<TextField select label="Type" value={type} onChange={(event) => setType(event.target.value)} sx={{ minWidth: 160 }}>
						<MenuItem value="">All types</MenuItem>
						{['User', 'Permission', 'Agent', 'System'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
					</TextField>
					<TextField label="Search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Actor, action, target, or details" fullWidth />
				</Stack>
			</Paper>

			<Box sx={{ flexGrow: 1, minHeight: 0 }}>
				<Paper sx={{ height: '100%', minHeight: 360, overflow: 'hidden' }}>
					<DataGrid rows={rows} columns={columns} getRowId={(row) => row.id} disableColumnFilter disableColumnMenu disableRowSelectionOnClick density={isMobile ? 'compact' : 'standard'} columnVisibilityModel={{ actor: !isMobile, details: !isMobile }} sx={{ border: 0 }} />
				</Paper>
			</Box>
		</Stack>
	);
}
